import { Link, useNavigate } from "@tanstack/react-router";
import { Bell, LogOut, Shield, User } from "lucide-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { authService } from "@/lib/services/authService";
import { useNotifications } from "@/hooks/use-notifications";

function NotificationBell() {
  const { unreadCount } = useNotifications();
  return (
    <Link
      to="/notifications"
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/20 text-white transition hover:bg-white/10 hover:border-white/40"
      aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
    >
      <Bell className="h-4 w-4" />
      {unreadCount > 0 && (
        <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-savings px-1 text-[10px] font-semibold leading-none text-savings-foreground h-[18px] ring-2 ring-navy">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </Link>
  );
}

export function SiteHeader() {
  const { session, roles, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const isStaff = roles.includes("staff") || roles.includes("admin");
  const workspacePath = isStaff ? "/operations" : "/dashboard";

  async function handleSignOut() {
    setMenuOpen(false);
    // Sign-out hygiene: stop in-flight queries and drop cached protected data
    // BEFORE clearing the session, so cached queries don't 401-storm after.
    await queryClient.cancelQueries();
    queryClient.clear();
    await authService.signOut();
    navigate({ to: "/login", replace: true });
  }

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-navy/95 text-navy-foreground backdrop-blur-md supports-[backdrop-filter]:bg-navy/80">
      <div className="container-page flex h-16 items-center justify-between gap-3">
        <Link
          to="/"
          aria-label="ClaimGuard — Home"
          className="group flex min-w-0 items-center gap-2.5 rounded-md"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 transition group-hover:bg-white/15">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold tracking-wide sm:text-sm">
              INVOICE INTELLIGENCE AI
            </div>
            <div className="hidden truncate text-[10px] uppercase tracking-[0.18em] text-white/60 sm:block">
              <span className="lg:hidden">AI Reviews • Full-Service Negotiation</span>
              <span className="hidden lg:inline">
                AI Invoice Reviews &amp; Full-Service Negotiation
              </span>
            </div>
          </div>
        </Link>
        <nav className="hidden items-center gap-1 text-sm md:flex">
          {[
            { hash: "how-it-works", label: "How It Works" },
            { hash: "what-you-receive", label: "What We Review" },
            { hash: "report", label: "What's Inside Your Report" },
            { hash: "pricing", label: "Pricing" },
            { hash: "faq", label: "FAQ" },
          ].map((item) => (
            <Link
              key={item.hash}
              to="/"
              hash={item.hash}
              className="rounded-md px-3 py-2 text-white/75 transition hover:bg-white/10 hover:text-white"
            >
              {item.label}
            </Link>
          ))}
          {session && (
            <Link
              to={workspacePath}
              className="rounded-md px-3 py-2 text-white/75 transition hover:bg-white/10 hover:text-white"
            >
              {isStaff ? "Operations" : "Dashboard"}
            </Link>
          )}
        </nav>
        <div className="flex items-center gap-2">
          {loading ? (
            <div className="h-10 w-24 shimmer rounded-md opacity-40" aria-hidden />
          ) : session ? (
            <>
              <NotificationBell />
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label={`Account menu for ${session.user.email ?? "your account"}`}
                  className="inline-flex h-10 min-w-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-3 text-sm font-medium text-white transition hover:bg-white/10 hover:border-white/40"
                >
                  <User className="h-4 w-4" aria-hidden />
                  <span className="hidden max-w-[160px] truncate sm:inline">
                    {session.user.email}
                  </span>
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border bg-card text-foreground shadow-elevated animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-150"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div className="border-b px-4 py-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Signed in as
                      </div>
                      <div className="mt-0.5 truncate text-sm font-medium">
                        {session.user.email}
                      </div>
                    </div>
                    <Link
                      to={workspacePath}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm transition hover:bg-accent"
                      role="menuitem"
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                      {isStaff ? "Operations Center" : "Dashboard"}
                    </Link>
                    <button
                      onClick={handleSignOut}
                      role="menuitem"
                      className="flex w-full items-center gap-2 border-t px-4 py-2.5 text-left text-sm transition hover:bg-accent"
                    >
                      <LogOut className="h-4 w-4 text-muted-foreground" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden h-10 items-center rounded-lg border border-white/20 px-4 text-sm font-medium text-white transition hover:bg-white/10 hover:border-white/40 sm:inline-flex"
              >
                Sign in
              </Link>
              <Link
                to="/submit"
                className="inline-flex h-10 items-center justify-center rounded-lg bg-savings px-4 text-sm font-semibold text-savings-foreground shadow-sm transition hover:opacity-90 hover:shadow-md"
              >
                Submit Your Invoice
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
